import posthog from 'posthog-js';
import { canMeasure, configureConsentEndpoint, getConsentStatus, getConsentUpdatedAt, getTrackingUuid, measurementEnvironment, measurementIsTest, subscribeConsent } from './consent';
import { attributionTouch, sanitizeProperties, UUID, type AttributionTouch } from './measurementCore';
export { posthog };
let started = false;
let site = 'web';
let surfaceReady = false;
export function measurementReady() { surfaceReady = true; captureLanding(); }
let consentEpoch = crypto.randomUUID();
// PostHog 1.363.6 opt-out does not cancel requests already queued. Guard its
// transport as well as capture, including a revoke/regrant before the next flush.
const sdkSend = posthog._send_request.bind(posthog);
posthog._send_request = options => {
  const stale = (record: unknown) => record && typeof record === 'object' && 'event' in record && (record as {properties?: Record<string, unknown>}).properties?.consent_epoch !== consentEpoch;
  if (Array.isArray(options.data)) options = {...options, data: options.data.filter(record => !stale(record))};
  if (!canMeasure() || stale(options.data) || Array.isArray(options.data) && options.data.length === 0) {
    options.callback?.({statusCode: 200, text: 'discarded_by_consent'}); return;
  }
  sdkSend(options);
};
let sessionId: string | undefined;
let firstTouch: AttributionTouch | undefined;
let conversionTouch: AttributionTouch | undefined;
const firstKey = 'parables:measurement:first-touch:v1';
let lastEntryUrl = '';
let lastTaggedHash = '';

let landingKey = '';
const captured = new Set<string>();
export function captureUXOnce(event: string, key: string, properties: Record<string, unknown> = {}): void { if (!canMeasure() || captured.has(`${event}:${key}`)) return; captured.add(`${event}:${key}`); captureUX(event, properties); }
function touches(): void {
  if (!canMeasure()) return;
  const entryUrl = `${location.pathname}${location.search}`;
  const taggedHash = location.hash.includes('?') ? location.hash.slice(location.hash.indexOf('?') + 1) : '';
  const newEntry = entryUrl !== lastEntryUrl || !!taggedHash && taggedHash !== lastTaggedHash;
  if (conversionTouch && !newEntry) return;
  const touch = attributionTouch(location.href, document.referrer);
  lastEntryUrl = entryUrl;
  if (taggedHash) lastTaggedHash = taggedHash;
  if (conversionTouch) {
    // A current route with new explicit tags is a new conversion touch. Moving
    // from preview to plans/checkout alone never creates another acquisition.
    if (touch.attributionStatus === 'tagged') conversionTouch = touch;
    return;
  }
  try {
    const first = JSON.parse(localStorage.getItem(firstKey) ?? 'null') as AttributionTouch | null;
    if (first?.occurredAt && !Number.isNaN(Date.parse(first.occurredAt)) && first.landingPath) firstTouch = first;
  } catch { /* Optional. */ }
  firstTouch ??= touch;
  conversionTouch = touch;
  try { localStorage.setItem(firstKey, JSON.stringify(firstTouch)); } catch { /* Optional. */ }
}

export type AnalyticsContext = { schemaVersion: number; environment: typeof measurementEnvironment; isTest: boolean; consentStatus: ReturnType<typeof getConsentStatus>; consentUpdatedAt?: string; trackingUuid: string; anonymousId?: string; sessionId?: string; firstTouch?: AttributionTouch; conversionTouch?: AttributionTouch };
export function analyticsContext(): AnalyticsContext {
  const consentStatus = getConsentStatus();
  const common: AnalyticsContext = { schemaVersion: 1, environment: measurementEnvironment, isTest: measurementIsTest, consentStatus, consentUpdatedAt: getConsentUpdatedAt(), trackingUuid: getTrackingUuid() };
  if (!canMeasure()) return common;
  touches();
  const id = started ? posthog.get_distinct_id() : undefined;
  sessionId ??= crypto.randomUUID();
  return { ...common, ...(id && UUID.test(id) ? { anonymousId: id } : {}), sessionId, firstTouch, conversionTouch };
}
function envelope() {
  const context = analyticsContext();
  return { event_id: crypto.randomUUID(), schema_version: 1, consent_epoch: consentEpoch, occurred_at: new Date().toISOString(), environment: measurementEnvironment, is_test: measurementIsTest, emitter: 'browser', platform: 'web', analytics_consent: getConsentStatus(), site, journey_id: context.trackingUuid, tracking_uuid: context.trackingUuid, session_id: 'sessionId' in context ? context.sessionId : undefined, first_touch: firstTouch, conversion_touch: conversionTouch };
}
export function captureUX(event: string, properties: Record<string, unknown> = {}): void { if (canMeasure() && started) posthog.capture(event, properties); }
export function captureLanding(): void {
  if (!surfaceReady || !canMeasure() || !started || document.visibilityState === 'hidden') return;
  touches();
  const route = attributionTouch(location.href, '').landingPath;
  const key = `${route}${site === 'app-lander' ? location.hash : ''}`;
  if (landingKey === key) return;
  landingKey = key;
  captureUX('landing_viewed', { route, story_id: conversionTouch?.storyId, landing_page_variant: new URL(location.href).searchParams.get('variant') ?? (site === 'app-lander' ? location.hash.slice(1) || 'default' : undefined) });
}
function syncConsent(): void {
  if (!canMeasure()) {
    if (started) { posthog.stopSessionRecording(); posthog.opt_out_capturing(); posthog.reset(); }
    consentEpoch = crypto.randomUUID();
    sessionId = undefined; firstTouch = undefined; conversionTouch = undefined; lastEntryUrl = ''; lastTaggedHash = ''; landingKey = ''; captured.clear();
    return;
  }
  touches();
  if (!started) {
    // Use explicit events only; replay and autocapture remain disabled.
    posthog.init(import.meta.env.VITE_POSTHOG_KEY || 'phc_mfjKfJZKVKc52GSavc8nvmXIDgIe45v9Mx9yIoDw7HI', {
      api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://c.readparables.com', ui_host: 'https://us.posthog.com', defaults: '2026-01-30',
      person_profiles: 'identified_only', capture_pageview: false, capture_pageleave: false, autocapture: false,
      disable_session_recording: true, disable_surveys: true, advanced_disable_feature_flags: true, advanced_disable_flags: true,
      cross_subdomain_cookie: false, persistence: 'localStorage', save_campaign_params: false, save_referrer: false,
      before_send(event) { if (!canMeasure() || !event || event.event === '$set') return null; delete event.$set; delete event.$set_once; event.properties = sanitizeProperties({ ...event.properties, ...envelope() }); return event; },
    });
    started = true;
  }
  posthog.opt_in_capturing({ captureEventName: false });
  captureLanding();
}
export function startMeasurement(siteName: string, apiBaseUrl: string): void { site = siteName; configureConsentEndpoint(apiBaseUrl); syncConsent(); }
subscribeConsent(syncConsent);
window.addEventListener('hashchange', captureLanding);
window.addEventListener('popstate', captureLanding);
document.addEventListener('visibilitychange', captureLanding);
